import { Observable, of, finalize, shareReplay, tap } from "rxjs";
import { GenerationError, ProcessEvent, polling } from "./polling";
import uuid from "short-unique-id";

export type PollingProcess = Observable<{ requestId: string }>;
export type JobProcess<TStart, TComplete> = {
  process: Observable<ProcessEvent<TStart, TComplete>>;
  jobId: string;
};

export interface IJobService<
  TInputData,
  TRetryData,
  TStartData,
  TDataComplete,
> {
  dispatch(data: TInputData): JobProcess<TStartData, TDataComplete>;

  retry(
    key: string,
    retryData: TRetryData,
  ): JobProcess<TStartData, TDataComplete>;

  currentProcess(
    key: string,
  ): Observable<ProcessEvent<TStartData, TDataComplete>>;

  isLoading(key: string): boolean;
}

export abstract class BaseJobPollingService<
  TInputData,
  TRetryData,
  TStartData,
  TDataComplete,
> implements IJobService<TInputData, TRetryData, TStartData, TDataComplete>
{
  protected currentJobs: Map<
    string,
    Observable<ProcessEvent<TStartData, TDataComplete>>
  >;
  protected currentJobsData: Map<
    string,
    { input: TInputData; start?: TStartData }
  >;

  constructor(
    protected polling_timeout: number,
    protected polling_delay: number,
  ) {
    this.currentJobs = new Map();
    this.currentJobsData = new Map();
  }

  isLoading(key: string): boolean {
    return this.currentJobs.has(key);
  }

  currentProcess(
    key: string,
  ): Observable<ProcessEvent<TStartData, TDataComplete>> {
    const currentJob = this.currentJobs.get(key);
    if (!currentJob) {
      return of({
        status: "not_started",
        data: null,
      });
    }
    return currentJob;
  }

  dispatch(data: TInputData): JobProcess<TStartData, TDataComplete> {
    const key = this.generateKey();
    return {
      jobId: key,
      process: this.preparePollingProcess(
        key,
        data,
        this._dispatchRequest(data),
      ),
    };
  }

  retry(
    key: string,
    retryData: TRetryData,
  ): JobProcess<TStartData, TDataComplete> {
    if (!this.currentJobsData.has(key)) {
      return {
        jobId: key,
        process: of({
          status: "error" as const,
          data: {
            error: "No generation data available to regenerate.",
            subject: GenerationError.GENERATION_FAILED,
          },
        }),
      };
    }
    const jobData = this.currentJobsData.get(key);
    return {
      jobId: key,
      process: this.preparePollingProcess(
        key,
        jobData.input,
        this._retryRequest(jobData, retryData),
      ),
    };
  }

  protected abstract _dispatchRequest(data: TInputData): PollingProcess;

  protected abstract _retryRequest(
    prevTryData: { input: TInputData; start?: TStartData },
    retryData: TRetryData,
  ): PollingProcess;

  protected abstract _checkStatusRequest(
    requestId: string,
  ): Observable<{ status: string; data: null }>;

  private preparePollingProcess(
    key: string,
    jobData: TInputData,
    jobProcess: Observable<{ requestId: string }>,
  ): Observable<ProcessEvent<TStartData, TDataComplete>> {
    if (this.currentJobs.has(key)) {
      return of({
        status: "error" as const,
        data: {
          error: "Job already in progress",
          subject: GenerationError.ALREADY_RUN,
        },
      });
    }
    const currentJob = jobProcess.pipe(
      tap({
        subscribe: () => {
          const data = {
            ...(this.currentJobsData.get(key) || {}),
            input: jobData,
          };
          this.currentJobsData.set(key, data);
        },
      }),
      polling(
        (requestId) => this._checkStatusRequest(requestId),
        this.polling_timeout,
        this.polling_delay,
      ),
      tap((event) => {
        if (event.status === "start") {
          const data = {
            ...(this.currentJobsData.get(key) || {}),
            start: event.data.data
          } as { input: TInputData; start: TStartData };
          this.currentJobsData.set(key, data);
        }
      }),
      finalize(() => {
        this.currentJobs.delete(key);
      }),
      shareReplay(1),
    ) as Observable<ProcessEvent<TStartData, TDataComplete>>;

    this.currentJobs.set(key, currentJob);
    return currentJob;
  }

  protected generateKey(): string {
    return new uuid().randomUUID();
  }
}
